import * as React from 'react';
import ReactLoading from 'react-loading';
import { useAuth0 } from '@auth0/auth0-react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import ContentCutRoundedIcon from '@mui/icons-material/ContentCutRounded';

import { Alert } from '@mui/material';
import { UserRole } from '../types/role';
import headerLogo from '../images/header-logo.png';
import StickyFooter from './components/StickyFooter';
import {
  darkPageBg,
  darkCardBg,
  darkFormContainerSx,
  darkButtonSx,
  accent,
} from './components/darkFormStyles';

interface RoleOption {
  value: UserRole;
  label: string;
  description: string;
  icon: React.ReactNode;
}

const ROLE_OPTIONS: RoleOption[] = [
  {
    value: UserRole.SHOP_OWNER,
    label: 'Shop Owner',
    description: 'Manage your shop, barbers and bookings',
    icon: <StoreRoundedIcon sx={{ fontSize: 30 }} />,
  },
  {
    value: UserRole.SHOP_BARBER,
    label: 'Shop Barber',
    description: 'Access your shop schedule and clients',
    icon: <GroupsRoundedIcon sx={{ fontSize: 30 }} />,
  },
  {
    value: UserRole.BARBER,
    label: 'Barber',
    description: 'Manage your independent calendar and gallery',
    icon: <ContentCutRoundedIcon sx={{ fontSize: 30 }} />,
  },
];

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithRedirect } = useAuth0();
  const [role, setRole] = React.useState<UserRole | ''>('');
  const [loading, setLoading] = React.useState(false);
  const [showError, setError] = React.useState({
    error: false,
    value: '',
  });
  // location.state only survives a client-side (React Router) navigation.
  // A failed role/partner lookup ends the Auth0 session via a full-page
  // logout redirect back to this route, which loses that state — so the
  // notice is also persisted in sessionStorage to survive the reload.
  const [notice, setNotice] = React.useState<string | null>(null);

  React.useEffect(() => {
    const storedNotice = sessionStorage.getItem('authNotice');
    if (storedNotice) {
      sessionStorage.removeItem('authNotice');
      setNotice(storedNotice);
    } else if (typeof location.state === 'string') {
      setNotice(location.state);
    }
  }, [location.state]);

  React.useEffect(() => {
    if (sessionStorage.getItem('user') != null) {
      navigate('/dashboard');
    }
  });

  const selectRole = (value: UserRole) => {
    localStorage.setItem('role', value);
    setRole(value);
  };

  const changeRole = () => {
    localStorage.removeItem('role');
    setRole('');
    setError({ error: false, value: '' });
  };

  // Redirects to Auth0's hosted login page using the Authorization Code
  // Flow with PKCE (handled automatically by the Auth0 React SDK). The
  // user enters their credentials on Auth0's domain, so the password is
  // never visible to, or handled by, this application.
  const handleSignIn = async () => {
    setError({ error: false, value: '' });
    setLoading(true);

    try {
      await loginWithRedirect({
        appState: { returnTo: '/authenticate' },
        // Force the hosted login form every time, instead of silently
        // reusing an existing Auth0 SSO session — otherwise a still-valid
        // session (e.g. one that wasn't fully cleared on logout) causes
        // Auth0 to redirect straight back to /dashboard without ever
        // showing the login page. @auth0/auth0-react is pinned to v1,
        // whose BaseLoginOptions expect `prompt` as a top-level flat
        // option, not nested under `authorizationParams` (a v2 shape).
        prompt: 'login',
      });
    } catch (error: any) {
      setLoading(false);
      setError({
        error: true,
        value: error?.description || 'Something went wrong. Please try again.',
      });
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: darkPageBg,
      }}
    >
      <Container
        component="main"
        maxWidth="xs"
        sx={{ ...darkFormContainerSx, p: 4, my: 8, flex: '1 0 auto' }}
      >
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src={headerLogo}
            alt="Barbers Limited"
            sx={{ width: 96, height: 96, objectFit: 'contain', mb: 1 }}
          />
          <Typography component="h1" variant="h5" sx={{ color: '#fff' }}>
            Sign in
          </Typography>

          {showError.error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {showError.value}
            </Alert>
          )}
          {notice !== null ? (
            <Alert
              severity={
                notice === 'You do not have permission' ? 'error' : 'success'
              }
              sx={{ mt: 2, width: '100%' }}
            >
              {notice}
            </Alert>
          ) : (
            ''
          )}

          {role === '' ? (
            <>
              <Typography
                variant="body2"
                sx={{
                  mt: 2,
                  mb: 1,
                  textAlign: 'center',
                  color: 'rgba(255,255,255,0.65)',
                }}
              >
                Select your role to continue
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  mt: 1,
                }}
              >
                {ROLE_OPTIONS.map((option) => (
                  <Box
                    key={option.value}
                    role="button"
                    aria-label={option.label}
                    tabIndex={0}
                    onClick={() => selectRole(option.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        selectRole(option.value);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: darkCardBg,
                      border: '1px solid rgba(255,255,255,0.1)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: accent,
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(221,135,9,0.2)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 52,
                        height: 52,
                        borderRadius: '50%',
                        flexShrink: 0,
                        color: '#fff',
                        background:
                          'linear-gradient(135deg, #dd8709 0%, #b86e08 100%)',
                      }}
                    >
                      {option.icon}
                    </Box>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography
                        sx={{
                          fontWeight: 'bold',
                          fontSize: 16,
                          color: '#fff',
                        }}
                      >
                        {option.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: 'rgba(255,255,255,0.55)' }}
                      >
                        {option.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
              <Grid container justifyContent="flex-end">
                <Grid sx={{ mt: 2 }}>
                  <Link
                    component={RouterLink}
                    to="/get-started"
                    variant="body2"
                    style={{
                      color: accent,
                      fontWeight: 'bold',
                      fontSize: 15,
                      textAlign: 'center',
                    }}
                  >
                    Don't have an account yet? Sign up
                  </Link>
                </Grid>
              </Grid>
            </>
          ) : (
            <Box sx={{ mt: 3, width: '100%' }}>
              <Button
                onClick={changeRole}
                startIcon={<ArrowBackIcon />}
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  textTransform: 'none',
                  mb: 1,
                  '&:hover': { color: accent, bgcolor: 'transparent' },
                }}
              >
                Change role
              </Button>
              <Typography
                sx={{
                  mb: 2,
                  fontSize: 15,
                  fontWeight: 'bold',
                  color: accent,
                }}
              >
                Signing in as {role}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: 3,
                  color: 'rgba(255,255,255,0.65)',
                }}
              >
                You'll be redirected to our secure sign-in page to enter your
                credentials.
              </Typography>
              <Button
                onClick={handleSignIn}
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  ...darkButtonSx,
                  p: 1.5,
                  width: '100%',
                }}
              >
                {!loading ? (
                  `Sign in as a ${role}`
                ) : (
                  <ReactLoading
                    type={'spin'}
                    color={'#fff'}
                    height={30}
                    width={30}
                  />
                )}
              </Button>
              <Grid container justifyContent="flex-end">
                <Grid sx={{ mt: 1 }}>
                  <Link
                    component={RouterLink}
                    to="/iforgot"
                    variant="body2"
                    style={{
                      color: accent,
                      fontWeight: 'bold',
                      fontSize: 15,
                      textAlign: 'center',
                    }}
                  >
                    Forgot Password?
                  </Link>
                </Grid>
              </Grid>
              <Grid container justifyContent="flex-end">
                <Grid sx={{ mt: 1 }}>
                  <Link
                    href="/get-started"
                    variant="body2"
                    style={{
                      color: accent,
                      fontWeight: 'bold',
                      fontSize: 15,
                      textAlign: 'center',
                    }}
                  >
                    Don't have an account yet? Sign up
                  </Link>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Container>
      <StickyFooter companyLinkName="Barbers Ltd" />
    </Box>
  );
}
